import { FC, ReactNode, useCallback } from 'react'
import ImageGallery from '@/app/[locale]/(public)/propiedades/[slug]/_components/ImageGallery'
import { MediaDTO } from '@/core/media/typings'
import CustomModal from '@/app/components/common/CustomModal'
import { useDisclosure } from '@chakra-ui/react'
import { gtmEvent, RealestateDetailGalleryClose, RealestateDetailGalleryOpen } from '@/utils/gtm'
import useTimer from '@/hooks/useTimer'
import { useClientLocale } from '@/hooks/use-locale-config'

interface ImageGalleryModalProps {
  medias: MediaDTO[]
  trigger: ReactNode
  index: number
}

const ImageGalleryModal: FC<ImageGalleryModalProps> = ({ index, medias, trigger }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [getGalleryTime, resetTimer] = useTimer(isOpen)
  const locale = useClientLocale()

  const handleOpen = useCallback(() => {
    resetTimer()
    gtmEvent<RealestateDetailGalleryOpen>({
      name: 'realestate_detail_gallery_open',
      locale,
      params: {}
    })

    onOpen()
  }, [resetTimer, locale, onOpen])

  const handleClose = useCallback(() => {
    const time = getGalleryTime()
    const timeSpent = Math.round(time / 1000)

    gtmEvent<RealestateDetailGalleryClose>({
      name: 'realestate_detail_gallery_close',
      locale,
      params: {
        duration_seconds: timeSpent
      }
    })

    resetTimer()
    onClose()
  }, [getGalleryTime, locale, resetTimer, onClose])

  return (
    <CustomModal
      size={{ base: 'full', md: 'full' }}
      bodyProps={{
        p: 1,
        maxW: 'full',
        overflow: 'hidden'
      }}
      onTriggerClick={handleOpen}
      trigger={trigger}
      onClose={handleClose}
      {...{ isOpen, onOpen }}>
      <ImageGallery medias={medias} index={index} />
    </CustomModal>
  )
}

export default ImageGalleryModal
